import React from 'react';
import { Box, Typography, Chip, Paper, Tooltip } from '@mui/material';

const LINEAR_ORDER = ['Content Ready', 'Content Approved', 'Planning Completed', 'Shoot Completed', 'Editing Completed', 'Released'];

const BASE_STEPS = [
  { status: 'Content Ready',      label: 'Content\nReady',      icon: '📝', timestampKey: 'contentReadyAt',      fallback: 'createdAt',   dateOnly: false },
  { status: 'Content Approved',   label: 'Content\nApproved',   icon: '✅', timestampKey: 'contentApprovedAt',   fallback: null,          dateOnly: false },
  { status: 'Planning Completed', label: 'Planning\nCompleted', icon: '📋', timestampKey: 'planningCompletedAt', fallback: null,          dateOnly: false },
  { status: 'Shoot Completed',    label: 'Shoot\nCompleted',    icon: '🎬', timestampKey: 'shootCompletedAt',    fallback: 'shootDate',   dateOnly: false },
  { status: 'Editing Completed',  label: 'Editing\nCompleted',  icon: '✂️', timestampKey: 'editingCompletedAt',  fallback: null,          dateOnly: false },
  { status: 'Released',           label: 'Released',            icon: '🚀', timestampKey: 'releasedAt',          fallback: 'releaseDate', dateOnly: false },
];

const REVISION_STEP = {
  status: 'Sent for Revision', label: 'Sent for\nRevision', icon: '🔁',
  timestampKey: 'sentForRevisionAt', fallback: null, dateOnly: false, isRevision: true,
};

// Revision always sits between Content Ready (idx 0) and Content Approved (idx 1)
// Only include it when the content actually went through revision
function buildSteps(content) {
  if (!content.sentForRevisionAt) return BASE_STEPS;
  const steps = [...BASE_STEPS];
  steps.splice(1, 0, REVISION_STEP); // insert at index 1
  return steps;
}

// ── Working hours calculator (09:30–18:30) ───────────────────────────────────
const WORK_START = 9 * 60 + 30;
const WORK_END   = 18 * 60 + 30;

function toMins(d) { return d.getHours() * 60 + d.getMinutes(); }

function workingMinutes(start, end) {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (e <= s) return 0;
  let total = 0;
  const cur = new Date(s); cur.setSeconds(0, 0);
  while (true) {
    const curDay = new Date(cur); curDay.setHours(0, 0, 0, 0);
    const isFirst = curDay.getTime() === new Date(s).setHours(0,0,0,0);
    const isLast  = curDay.getTime() === new Date(e).setHours(0,0,0,0);
    const dayStart = isFirst ? Math.max(WORK_START, toMins(s)) : WORK_START;
    const dayEnd   = isLast  ? Math.min(WORK_END,   toMins(e)) : WORK_END;
    total += Math.max(0, dayEnd - dayStart);
    if (isLast) break;
    cur.setDate(cur.getDate() + 1);
  }
  return total;
}

function fmtDuration(mins) {
  if (mins === null || mins < 0) return null;
  if (mins === 0) return '< 1m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  const days = Math.floor(h / 9);
  const remH = h % 9;
  if (days > 0) {
    if (remH === 0 && m === 0) return `${days}d`;
    if (m === 0) return `${days}d ${remH}h`;
    return `${days}d ${remH}h ${m}m`;
  }
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// Returns true if value is a bare date string "YYYY-MM-DD" with no time component
function isDateOnly(val) {
  return typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val);
}

// Parse a value to a Date — for date-only strings, assume 10:00 AM local time
function parseDate(val) {
  if (!val) return null;
  if (isDateOnly(val)) {
    const [y, mo, d] = val.split('-').map(Number);
    return new Date(y, mo - 1, d, 10, 0, 0);
  }
  return new Date(val);
}

// Format any date/datetime value for display
// Date-only values show with assumed ", 10:00" so it's clear a default was used
function fmtAuto(val) {
  if (!val) return null;
  try {
    const d = parseDate(val);
    const dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = isDateOnly(val) ? '10:00 *' : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    return `${dateStr}, ${timeStr}`;
  } catch { return null; }
}

function getTimestamp(step, content, state) {
  const raw = step.timestampKey ? content[step.timestampKey] : null;
  const fallbackVal = (state !== 'pending' && step.fallback) ? content[step.fallback] : null;
  const val = raw || fallbackVal;
  return val ? parseDate(val) : null;
}

function getState(step, currentStatus, content) {
  if (step.isRevision) return 'revision'; // only present when sentForRevisionAt exists
  const curLinearIdx = LINEAR_ORDER.indexOf(currentStatus);
  const stepLinearIdx = LINEAR_ORDER.indexOf(step.status);
  const effectiveIdx = currentStatus === 'Sent for Revision'
    ? LINEAR_ORDER.indexOf('Planning Completed')
    : curLinearIdx;
  if (effectiveIdx > stepLinearIdx) return 'done';
  if (effectiveIdx === stepLinearIdx) return 'active';
  return 'pending';
}

const STATUS_BG = {
  'Released':          { bg: '#e8f5e9', color: '#2e7d32' },
  'Editing Completed': { bg: '#ede7f6', color: '#6a1b9a' },
  'Shoot Completed':   { bg: '#fff3e0', color: '#e65100' },
  'Content Approved':  { bg: '#e3f2fd', color: '#1565c0' },
  'Sent for Revision': { bg: '#fce4ec', color: '#c62828' },
};

export default function ContentRoadmap({ content, compact = false }) {
  if (!content) return null;

  const currentStatus = content.status;
  const cs = STATUS_BG[currentStatus] || { bg: '#f5f5f5', color: '#424242' };
  const size = compact ? 36 : 42;

  // Build steps with revision inserted at the correct position
  const STEPS = buildSteps(content);

  // Pre-compute working-hour durations for each connector
  const durations = STEPS.map((step, idx) => {
    if (idx === STEPS.length - 1) return null;
    const nextStep = STEPS[idx + 1];
    const leftState  = getState(step,     currentStatus, content);
    const rightState = getState(nextStep, currentStatus, content);
    // Only calculate if both sides have been reached
    if (leftState === 'pending' || rightState === 'pending') return null;
    const t1 = getTimestamp(step,     content, leftState);
    const t2 = getTimestamp(nextStep, content, rightState);
    if (!t1 || !t2) return null;
    return fmtDuration(workingMinutes(t1, t2));
  });

  function connectorDone(leftStep, rightStep) {
    if (leftStep.isRevision || rightStep.isRevision) return false;
    return getState(leftStep, currentStatus, content) === 'done';
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: compact ? 1.5 : 2, bgcolor: '#fafafa', borderRadius: 2, overflowX: 'auto' }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={compact ? 1.5 : 2} flexWrap="wrap">
        <Typography variant="caption" fontWeight={700} color="text.secondary" letterSpacing={1}>
          PROGRESS ROADMAP
        </Typography>
        <Chip
          label={currentStatus}
          size="small"
          sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: cs.bg, color: cs.color }}
        />
      </Box>

      {/* Steps + connectors */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', minWidth: compact ? 560 : 620 }}>
        {STEPS.map((step, idx) => {
          const state = getState(step, currentStatus, content);
          const rawVal = step.timestampKey ? content[step.timestampKey] : null;
          const fallbackVal = (state !== 'pending' && step.fallback) ? content[step.fallback] : null;
          const dateStr = fmtAuto(rawVal || fallbackVal);

          const circleBg =
            state === 'done'     ? '#2e7d32' :
            state === 'active'   ? '#1565c0' :
            state === 'revision' ? '#e65100' : '#f5f5f5';

          const circleBorder =
            state === 'done'     ? '#2e7d32' :
            state === 'active'   ? '#1565c0' :
            state === 'revision' ? '#e65100' : '#bdbdbd';

          const labelColor =
            state === 'done'     ? '#2e7d32' :
            state === 'active'   ? '#1565c0' :
            state === 'revision' ? '#e65100' : '#9e9e9e';

          const duration = idx < STEPS.length - 1 ? durations[idx] : null;
          const isDone = idx < STEPS.length - 1 && connectorDone(step, STEPS[idx + 1]);

          return (
            <React.Fragment key={`${step.status}-${idx}`}>
              <Tooltip
                arrow
                title={
                  <Box>
                    <Typography variant="caption" fontWeight={700} display="block">{step.status}</Typography>
                    {dateStr
                      ? <Typography variant="caption" display="block">
                          {dateStr}{dateStr.includes('*') ? ' (assumed 10:00 AM)' : ''}
                        </Typography>
                      : <Typography variant="caption" display="block" sx={{ opacity: 0.6 }}>
                          {state === 'pending' ? 'Not reached' : 'Date N/A'}
                        </Typography>
                    }
                  </Box>
                }
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 80, flexShrink: 0 }}>
                  <Box sx={{
                    width: size, height: size, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: state === 'pending' ? '#f5f5f5' : circleBg,
                    border: `2px solid ${circleBorder}`,
                    fontSize: compact ? '1rem' : '1.1rem',
                    boxShadow: state === 'active'   ? '0 0 0 4px rgba(21,101,192,0.15)' :
                               state === 'revision' ? '0 0 0 4px rgba(230,81,0,0.15)'   : 'none',
                    transition: 'all 0.2s',
                  }}>
                    {step.icon}
                  </Box>

                  <Typography variant="caption" fontWeight={state === 'active' || state === 'revision' ? 700 : 500}
                    textAlign="center"
                    sx={{ mt: 0.5, color: labelColor, fontSize: '0.63rem', lineHeight: 1.3, whiteSpace: 'pre-line' }}
                  >
                    {step.label}
                  </Typography>

                  {dateStr
                    ? dateStr.includes(',')
                      ? <>
                          <Typography variant="caption" textAlign="center" sx={{ fontSize: '0.58rem', mt: 0.25, color: '#616161', lineHeight: 1.2 }}>
                            {dateStr.split(',')[0]}
                          </Typography>
                          <Typography variant="caption" textAlign="center" sx={{ fontSize: '0.57rem', color: '#9e9e9e', lineHeight: 1.2 }}>
                            {dateStr.split(',')[1].trim()}
                          </Typography>
                        </>
                      : <Typography variant="caption" textAlign="center" sx={{ fontSize: '0.58rem', mt: 0.25, color: '#616161', lineHeight: 1.2 }}>
                          {dateStr}
                        </Typography>
                    : <Typography variant="caption" textAlign="center" sx={{ fontSize: '0.58rem', mt: 0.25, color: '#bdbdbd', lineHeight: 1.2 }}>
                        —
                      </Typography>
                  }
                </Box>
              </Tooltip>

              {/* Connector with duration badge */}
              {idx < STEPS.length - 1 && (
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', mt: `${(size / 2) - 7}px`, minWidth: 16 }}>
                  {duration ? (
                    <Tooltip arrow title="Working hours between these stages (09:30–18:30)">
                      <Typography variant="caption" sx={{
                        fontSize: '0.57rem', fontWeight: 600,
                        color: isDone ? '#2e7d32' : '#9e9e9e',
                        bgcolor: isDone ? '#f1f8e9' : '#f5f5f5',
                        border: `1px solid ${isDone ? '#a5d6a7' : '#e0e0e0'}`,
                        borderRadius: '8px', px: 0.6, py: 0.1, mb: 0.3,
                        whiteSpace: 'nowrap', lineHeight: 1.6,
                      }}>
                        {duration}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Box sx={{ height: 14 }} />
                  )}
                  <Box sx={{
                    width: '100%', height: 3,
                    bgcolor: isDone ? '#2e7d32' : '#e0e0e0',
                    borderRadius: 2, transition: 'background-color 0.3s',
                  }} />
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Paper>
  );
}
